// IdentityDetail.tsx
import React, { useState } from 'react';
import { X, Check, AlertTriangle, Maximize2, Download, XCircle, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import type { IdentityVerification } from '../../types';
import { apiClient } from '../../api/client';

interface IdentityDetailProps {
    item: IdentityVerification;
    onClose: () => void;
    onVerify: (id: number, approved: boolean, reason?: string) => Promise<void>;
}

export const IdentityDetail: React.FC<IdentityDetailProps> = ({ item, onClose, onVerify }) => {
    const [showConfirm, setShowConfirm] = useState(false);
    const [decision, setDecision] = useState<'approve' | 'reject' | null>(null);
    const [rejectReason, setRejectReason] = useState('');
    const [imageZoom, setImageZoom] = useState(false);
    const [customReason, setCustomReason] = useState('');
    const imageUrl = item.id_document_path.startsWith('/')
        ? `${apiClient.defaults.baseURL?.replace('/api', '')}${item.id_document_path}`
        : `${apiClient.defaults.baseURL?.replace('/api', '')}/media/id_documents/${item.id_document_path}`;
    const handleDecision = async () => {
        try {
            await onVerify(item.id, decision === 'approve', rejectReason);
            toast.success(`Identity ${decision === 'approve' ? 'approved' : 'rejected'}`);
            onClose();
        } catch (error) {
            toast.error('Failed to process verification');
        }
    };
    console.log(item.id_document_path)
    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4">
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />

                <div className="relative bg-white rounded-lg max-w-4xl w-full p-6">
                    {/* Header */}
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-medium">Identity Verification</h3>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                            <X size={20} />
                        </button>
                    </div>

                    {/* User Info */}
                    <div className="bg-gray-50 p-4 rounded-lg mb-4">
                        <div className="flex items-center">
                            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                                <span className="text-lg font-medium text-blue-600">
                                    {item.first_name[0]}{item.last_name[0]}
                                </span>
                            </div>
                            <div className="ml-4">
                                <h4 className="font-medium">{item.first_name} {item.last_name}</h4>
                                <p className="text-sm text-gray-500">{item.email}</p>
                                <p className="text-xs text-gray-400 mt-1">
                                    Submitted: {new Date(item.id_uploaded_at).toLocaleString()}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* ID Document */}
                    <div className="mb-6">
                        <div className="flex justify-between items-center mb-2">
                            <label className="block text-sm font-medium text-gray-700">
                                Identification Document
                            </label>
                            <div className="flex space-x-2">
                                <button
                                    onClick={() => setImageZoom(!imageZoom)}
                                    className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                                    title="Zoom"
                                >
                                    <Maximize2 size={18} />
                                </button>
                                <a
                                    href={item.id_document_path}
                                    download
                                    className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                                    title="Download"
                                >
                                    <Download size={18} />
                                </a>
                            </div>
                        </div>
                        <div
                            className={`border rounded-lg overflow-hidden cursor-pointer ${imageZoom ? 'fixed inset-10 z-50 bg-white p-4' : 'h-96'
                                }`}
                            onClick={() => setImageZoom(!imageZoom)}
                        >
                            <img
                                src={imageUrl}
                                alt="ID Document"
                                className="w-full h-full object-contain"
                            />
                        </div>
                    </div>

                    {/* Action Buttons */}
                    {!showConfirm ? (
                        <div className="flex justify-end space-x-3">
                            {item.id_verification_status === 'pending' ? (
                                // Pending - show action buttons
                                <>
                                    <button
                                        onClick={() => {
                                            setDecision('reject');
                                            setShowConfirm(true);
                                        }}
                                        className="px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50"
                                    >
                                        <X size={18} className="inline mr-2" />
                                        Reject
                                    </button>
                                    <button
                                        onClick={() => {
                                            setDecision('approve');
                                            setShowConfirm(true);
                                        }}
                                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                                    >
                                        <Check size={18} className="inline mr-2" />
                                        Approve
                                    </button>
                                </>
                            ) : item.id_verification_status === 'approved' ? (
                                // Already approved - show disabled approved button
                                <button
                                    disabled
                                    className="px-4 py-2 bg-green-100 text-green-700 rounded-lg cursor-default border border-green-200 flex items-center"
                                >
                                    <CheckCircle size={18} className="mr-2" />
                                    Already Approved
                                </button>
                            ) : (
                                // Already rejected - show disabled rejected button
                                <button
                                    disabled
                                    className="px-4 py-2 bg-red-100 text-red-700 rounded-lg cursor-default border border-red-200 flex items-center"
                                >
                                    <XCircle size={18} className="mr-2" />
                                    Already Rejected
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="border-t pt-4">
                            {decision === 'reject' && (
                                <div className="mb-4 space-y-3">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Reason for rejection <span className="text-red-500">*</span>
                                    </label>

                                    <select
                                        value={rejectReason}
                                        onChange={(e) => setRejectReason(e.target.value)}
                                        className="w-full px-3 py-2 border rounded-lg"
                                        autoFocus
                                    >
                                        <option value="">Select a reason...</option>
                                        <option value="Image too blurry/unclear">Image too blurry/unclear</option>
                                        <option value="Name does not match account">Name does not match account</option>
                                        <option value="ID appears expired">ID appears expired</option>
                                        <option value="ID information incomplete">ID information incomplete</option>
                                        <option value="Wrong ID type submitted">Wrong ID type submitted</option>
                                        <option value="Document appears altered/suspicious">Document appears altered/suspicious</option>
                                        <option value="other">Other (specify)</option>
                                    </select>

                                    {rejectReason === 'other' && (
                                        <input
                                            type="text"
                                            value={customReason}
                                            onChange={(e) => setCustomReason(e.target.value)}
                                            placeholder="Please specify..."
                                            className="w-full px-3 py-2 border rounded-lg"
                                        />
                                    )}
                                </div>
                            )}
                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={() => setShowConfirm(false)}
                                    className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDecision}
                                    disabled={decision === 'reject' && !rejectReason.trim()}
                                    className={`px-4 py-2 rounded-lg text-white ${decision === 'approve'
                                        ? 'bg-green-600 hover:bg-green-700'
                                        : 'bg-red-600 hover:bg-red-700'
                                        } disabled:opacity-50`}
                                >
                                    Confirm {decision === 'approve' ? 'Approval' : 'Rejection'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};